import React, { useEffect, useMemo, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import {
  Receipt, Save, Loader2, Play, BellRing, CheckCircle2, Ban, Settings2,
  FileText, AlertTriangle, CalendarClock, RotateCcw, XCircle,
  Mail, MessageSquare, Download, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import WhatsAppConnectButton from '@/components/WhatsAppConnectButton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const d = (s) => (s ? new Date(s).toLocaleDateString('de-CH') : '—');

const STATUS = {
  open: { label: 'Offen', cls: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Bezahlt', cls: 'bg-emerald-100 text-emerald-700' },
  overdue: { label: 'Überfällig', cls: 'bg-red-100 text-red-700' },
  void: { label: 'Storniert', cls: 'bg-slate-100 text-slate-500' },
};
const SUB_STATUS = {
  active: 'Aktiv', trialing: 'Testphase', canceling: 'In Kündigung',
  cancelled: 'Beendet', none: 'Kein Abo',
};

const Card = ({ children, className = '' }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>{children}</div>
);

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-slate-900 focus:outline-none';

export default function BillingPage() {
  const [settings, setSettings] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [dunning, setDunning] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [selTenant, setSelTenant] = useState('');
  const [interval, setInterval] = useState('month');
  const [preview, setPreview] = useState(null);
  const [tState, setTState] = useState(null);
  const [email, setEmail] = useState(null);
  const [wa, setWa] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testWaTo, setTestWaTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [s, inv, dun, ts, em, wset] = await Promise.all([
        superAdminApi.billingSettings(),
        superAdminApi.billingInvoices(),
        superAdminApi.billingDunning(),
        superAdminApi.listTenants(),
        superAdminApi.billingEmailSettings(),
        superAdminApi.billingWhatsappSettings(),
      ]);
      setSettings(s.data);
      setInvoices(inv.data.invoices || []);
      setDunning(dun.data.events || []);
      setTenants(ts.data || []);
      setEmail(em.data);
      setWa(wset.data);
    } catch { toast.error('Abrechnungsdaten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const billableTenants = useMemo(
    () => tenants.filter((t) => !t.all_modules_active),
    [tenants],
  );

  const setS = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  const saveSettings = async () => {
    setSaving('settings');
    try {
      await superAdminApi.saveBillingSettings({
        charge_mode: settings.charge_mode,
        notice_period_months: Number(settings.notice_period_months),
        mwst_enabled: !!settings.mwst_enabled,
        mwst_rate: Number(settings.mwst_rate),
        payment_term_days: Number(settings.payment_term_days),
        reminder_days: String(settings.reminder_days).split(',').map((x) => parseInt(x, 10)).filter((x) => x > 0),
        suspend_enabled: !!settings.suspend_enabled,
        suspend_after_days: Number(settings.suspend_after_days),
        yearly_discount_percent: Number(settings.yearly_discount_percent),
        setup_fee_chf: Number(settings.setup_fee_chf),
        whatsapp_fees_enabled: !!settings.whatsapp_fees_enabled,
      });
      toast.success('Einstellungen gespeichert');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const runBilling = async () => {
    setSaving('run');
    try {
      const r = await superAdminApi.billingRun('month');
      toast.success(`Abrechnung: ${r.data.created.length} Rechnung(en) erstellt`);
      await load();
    } catch { toast.error('Abrechnung fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const runDunning = async () => {
    setSaving('dunning');
    try {
      const r = await superAdminApi.billingDunningRun();
      toast.success(`Mahnlauf: ${r.data.events} Ereignis(se)`);
      await load();
    } catch { toast.error('Mahnlauf fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const markPaid = async (id) => {
    setSaving('inv-' + id);
    try { await superAdminApi.billingMarkPaid(id); toast.success('Als bezahlt markiert'); await load(); }
    catch { toast.error('Aktion fehlgeschlagen'); } finally { setSaving(''); }
  };
  const voidInv = async (id) => {
    setSaving('inv-' + id);
    try { await superAdminApi.billingVoid(id); toast.success('Rechnung storniert'); await load(); }
    catch { toast.error('Aktion fehlgeschlagen'); } finally { setSaving(''); }
  };
  const sendInvoiceEmail = async (inv) => {
    setSaving('mail-' + inv.id);
    try { const r = await superAdminApi.billingSendInvoiceEmail(inv.id); toast.success(`Rechnung ${r.data.number} per E-Mail gesendet`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'E-Mail konnte nicht gesendet werden'); } finally { setSaving(''); }
  };

  const loadTenant = async (tid) => {
    setSelTenant(tid);
    setPreview(null); setTState(null);
    if (!tid) return;
    try {
      const [p, st] = await Promise.all([
        superAdminApi.billingTenantPreview(tid, interval),
        superAdminApi.billingTenantState(tid),
      ]);
      setPreview(p.data); setTState(st.data);
    } catch { toast.error('Mandanten-Vorschau fehlgeschlagen'); }
  };

  const refreshPreview = async (iv) => {
    setInterval(iv);
    if (!selTenant) return;
    try { const p = await superAdminApi.billingTenantPreview(selTenant, iv); setPreview(p.data); }
    catch { /* noop */ }
  };

  const genTenant = async () => {
    setSaving('gen');
    try {
      const r = await superAdminApi.billingTenantGenerate(selTenant, interval);
      toast.success(r.data._skipped ? `Rechnung ${r.data.number} existiert bereits` : `Rechnung ${r.data.number} erstellt`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erstellen fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const cancelTenant = async () => {
    setSaving('cancel');
    try { const r = await superAdminApi.billingTenantCancel(selTenant); toast.success(`Kündigung wirksam zum ${d(r.data.effective_at)}`); await loadTenant(selTenant); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Kündigung fehlgeschlagen'); } finally { setSaving(''); }
  };
  const revokeCancel = async () => {
    setSaving('cancel');
    try { await superAdminApi.billingTenantRevokeCancel(selTenant); toast.success('Kündigung zurückgenommen'); await loadTenant(selTenant); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen'); } finally { setSaving(''); }
  };

  const setE = (k, v) => setEmail((p) => ({ ...p, [k]: v }));
  const setW = (k, v) => setWa((p) => ({ ...p, [k]: v }));

  const saveEmail = async () => {
    setSaving('email');
    try {
      const payload = {
        enabled: !!email.enabled,
        smtp_host: email.smtp_host || '', smtp_port: Number(email.smtp_port) || 587,
        smtp_username: email.smtp_username || '',
        smtp_from_email: email.smtp_from_email || '', smtp_from_name: email.smtp_from_name || '',
        reply_to: email.reply_to || '',
      };
      if (email.smtp_password) payload.smtp_password = email.smtp_password;
      const r = await superAdminApi.saveBillingEmailSettings(payload);
      setEmail(r.data); toast.success('Invicta-E-Mail-Fallback gespeichert');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const saveWa = async () => {
    setSaving('wa');
    try {
      const payload = {
        enabled: !!wa.enabled, phone_id: wa.phone_id || '',
        display_number: wa.display_number || '',
        business_account_id: wa.business_account_id || '',
      };
      if (wa.access_token) payload.access_token = wa.access_token;
      const r = await superAdminApi.saveBillingWhatsappSettings(payload);
      setWa(r.data); toast.success('Invicta-WhatsApp-Fallback gespeichert');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const sendTestEmail = async () => {
    if (!testEmailTo.trim()) { toast.error('Bitte eine Empfänger-E-Mail angeben'); return; }
    setSaving('test-email');
    try {
      await superAdminApi.testBillingEmail({ to_email: testEmailTo.trim() });
      toast.success('Test-E-Mail gesendet an ' + testEmailTo.trim());
    } catch (e) { toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const sendTestWa = async () => {
    if (!testWaTo.trim()) { toast.error('Bitte eine Empfänger-Nummer angeben'); return; }
    setSaving('test-wa');
    try {
      await superAdminApi.testBillingWhatsapp({ to_phone: testWaTo.trim() });
      toast.success('Test-WhatsApp gesendet an ' + testWaTo.trim());
    } catch (e) { toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const downloadPdf = async (inv) => {
    setSaving('pdf-' + inv.id);
    try {
      const r = await superAdminApi.billingInvoicePdf(inv.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.number}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF konnte nicht geladen werden'); }
    finally { setSaving(''); }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-20 text-slate-400" data-testid="billing-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const open = invoices.filter((i) => i.status === 'open' || i.status === 'overdue');
  const openSum = open.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="sa-billing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Receipt className="h-6 w-6" /> Abrechnung
          </h1>
          <p className="mt-1 text-sm text-slate-500">Plattform-Verrechnung (Mandant → InvictaX): echte Rechnungen aus Modulen & Konditionen, Proration, Kündigungsfrist und Mahnläufe.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="billing-run-btn" onClick={runBilling} disabled={saving === 'run'}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
            {saving === 'run' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Jetzt abrechnen
          </button>
          <button data-testid="billing-dunning-btn" onClick={runDunning} disabled={saving === 'dunning'}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {saving === 'dunning' ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Mahnlauf starten
          </button>
        </div>
      </div>

      {/* Kennzahlen */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card><div className="text-xs text-slate-500">Rechnungen gesamt</div><div className="mt-1 text-2xl font-bold text-slate-900" data-testid="kpi-invoices">{invoices.length}</div></Card>
        <Card><div className="text-xs text-slate-500">Offen / überfällig</div><div className="mt-1 text-2xl font-bold text-amber-600" data-testid="kpi-open">{open.length}</div></Card>
        <Card><div className="text-xs text-slate-500">Offene Summe</div><div className="mt-1 text-2xl font-bold text-slate-900" data-testid="kpi-open-sum">{chf(openSum)}</div></Card>
        <Card><div className="text-xs text-slate-500">Mahn-Ereignisse</div><div className="mt-1 text-2xl font-bold text-red-600" data-testid="kpi-dunning">{dunning.length}</div></Card>
      </div>

      {/* Einstellungen */}
      <Card>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900"><Settings2 className="h-5 w-5" /> Abrechnungs-Einstellungen</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Abbuchungs-Modus" hint="Echte Abbuchung benötigt eine verbundene Zahlungsanbindung.">
            <select data-testid="set-charge-mode" value={settings.charge_mode} onChange={(e) => setS('charge_mode', e.target.value)} className={inputCls}>
              <option value="invoice_only">Nur Rechnungen erstellen</option>
              <option value="auto_charge">Echt abbuchen</option>
            </select>
          </Field>
          <Field label="Kündigungsfrist (Monate)" hint="Grundabo; Module zum Monatsende abschaltbar.">
            <input data-testid="set-notice" type="number" min="0" value={settings.notice_period_months} onChange={(e) => setS('notice_period_months', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Zahlungsziel (Tage)">
            <input data-testid="set-term" type="number" min="0" value={settings.payment_term_days} onChange={(e) => setS('payment_term_days', e.target.value)} className={inputCls} />
          </Field>
          <Field label="MWST" hint="Standardmässig aus (noch nicht MWST-pflichtig).">
            <div className="flex items-center gap-3 pt-2">
              <button data-testid="set-mwst-toggle" onClick={() => setS('mwst_enabled', !settings.mwst_enabled)}
                className={`relative h-6 w-11 rounded-full transition ${settings.mwst_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${settings.mwst_enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
              <span className="text-sm text-slate-600">{settings.mwst_enabled ? 'Aktiv' : 'Aus'}</span>
            </div>
          </Field>
          <Field label="MWST-Satz (%)">
            <input data-testid="set-mwst-rate" type="number" step="0.1" min="0" value={settings.mwst_rate} onChange={(e) => setS('mwst_rate', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Jahresrabatt (%)" hint="Rabatt auf die Jahresrechnung.">
            <input data-testid="set-yearly-disc" type="number" step="0.1" min="0" value={settings.yearly_discount_percent} onChange={(e) => setS('yearly_discount_percent', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Mahn-Erinnerungen (Tage nach Fälligkeit)" hint="Kommagetrennt, z.B. 7,14">
            <input data-testid="set-reminders" value={settings.reminder_days} onChange={(e) => setS('reminder_days', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Sperren nach (Tagen)">
            <input data-testid="set-suspend-days" type="number" min="0" value={settings.suspend_after_days} onChange={(e) => setS('suspend_after_days', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Zugang sperren" hint="Bei anhaltendem Zahlungsverzug.">
            <div className="flex items-center gap-3 pt-2">
              <button data-testid="set-suspend-toggle" onClick={() => setS('suspend_enabled', !settings.suspend_enabled)}
                className={`relative h-6 w-11 rounded-full transition ${settings.suspend_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${settings.suspend_enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
              <span className="text-sm text-slate-600">{settings.suspend_enabled ? 'Ja' : 'Nein'}</span>
            </div>
          </Field>
          <Field label="WhatsApp-Gebühren verrechnen" hint="Meta-Konversationskosten je Betrieb als eigene Position auf die Monatsrechnung setzen. Sätze im Bereich WhatsApp-Gebühren.">
            <div className="flex items-center gap-3 pt-2">
              <button data-testid="set-whatsapp-fees-toggle" onClick={() => setS('whatsapp_fees_enabled', !settings.whatsapp_fees_enabled)}
                className={`relative h-6 w-11 rounded-full transition ${settings.whatsapp_fees_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${settings.whatsapp_fees_enabled ? 'left-5' : 'left-0.5'}`} />
              </button>
              <span className="text-sm text-slate-600">{settings.whatsapp_fees_enabled ? 'Aktiv' : 'Aus'}</span>
            </div>
          </Field>
        </div>
        <div className="mt-4">
          <button data-testid="set-save-btn" onClick={saveSettings} disabled={saving === 'settings'}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
            {saving === 'settings' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Einstellungen speichern
          </button>
        </div>
      </Card>

      {/* Pro Mandant */}
      <Card>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900"><FileText className="h-5 w-5" /> Rechnung pro Mandant</h2>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Mandant">
            <select data-testid="billing-tenant-select" value={selTenant} onChange={(e) => loadTenant(e.target.value)} className={`${inputCls} min-w-[220px]`}>
              <option value="">— wählen —</option>
              {billableTenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </Field>
          <Field label="Intervall">
            <select data-testid="billing-interval" value={interval} onChange={(e) => refreshPreview(e.target.value)} className={inputCls}>
              <option value="month">Monat</option>
              <option value="year">Jahr</option>
            </select>
          </Field>
          {selTenant && (
            <button data-testid="billing-generate-btn" onClick={genTenant} disabled={saving === 'gen'}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              {saving === 'gen' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Rechnung erstellen
            </button>
          )}
        </div>

        {preview && (
          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="px-3 py-2">Position</th><th className="px-3 py-2 text-right">Anteil</th><th className="px-3 py-2 text-right">Betrag</th>
                </tr></thead>
                <tbody>
                  {preview.positions.map((p) => (
                    <tr key={p.key} className="border-b border-slate-100">
                      <td className="px-3 py-2 text-slate-700">{p.name} {p.note && <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">{p.note}</span>}</td>
                      <td className="px-3 py-2 text-right text-slate-400">{Math.round((p.proration || 1) * 100)}%</td>
                      <td className="px-3 py-2 text-right text-slate-800">{chf(p.price)}</td>
                    </tr>
                  ))}
                  {preview.applied.map((a, i) => (
                    <tr key={'a' + i} className="border-b border-slate-100 bg-slate-50/60">
                      <td className="px-3 py-2 text-slate-500" colSpan={2}>{a.label}{a.reason ? ` — ${a.reason}` : ''}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{a.effect == null ? '' : chf(a.effect)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.whatsapp_detail && preview.whatsapp_detail.lines?.length > 0 && (
              <div data-testid="preview-whatsapp-detail" className="lg:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-emerald-800">
                  <MessageSquare className="h-3.5 w-3.5" /> Nachweis WhatsApp-Konversationen
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[11px] text-slate-500">
                    <th className="py-1">Kategorie</th>
                    <th className="py-1 text-right">Anzahl</th>
                    <th className="py-1 text-right">Satz</th>
                    <th className="py-1 text-right">Betrag</th>
                  </tr></thead>
                  <tbody>
                    {preview.whatsapp_detail.lines.map((ln) => (
                      <tr key={ln.category} className="border-t border-emerald-100">
                        <td className="py-1 text-slate-700">{ln.label}</td>
                        <td className="py-1 text-right text-slate-600">{ln.count}</td>
                        <td className="py-1 text-right text-slate-500">{chf(ln.rate)}</td>
                        <td className="py-1 text-right text-slate-800">{chf(ln.cost)}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-emerald-100 text-slate-500">
                      <td className="py-1" colSpan={3}>Zwischensumme ({preview.whatsapp_detail.conversations} Konversationen)</td>
                      <td className="py-1 text-right">{chf(preview.whatsapp_detail.base_cost)}</td>
                    </tr>
                    {preview.whatsapp_detail.markup_percent > 0 && (
                      <tr className="text-slate-500">
                        <td className="py-1" colSpan={3}>zzgl. Aufschlag {Math.round(preview.whatsapp_detail.markup_percent)}%</td>
                        <td className="py-1 text-right">{chf((preview.whatsapp_detail.amount || 0) - (preview.whatsapp_detail.base_cost || 0))}</td>
                      </tr>
                    )}
                    <tr className="border-t border-emerald-300 font-semibold text-emerald-800">
                      <td className="py-1" colSpan={3}>Weiterverrechnet</td>
                      <td className="py-1 text-right" data-testid="preview-whatsapp-total">{chf(preview.whatsapp_detail.amount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Listenpreis</span><span>{chf(preview.base_total)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Netto {preview.interval === 'year' ? '(Jahr)' : '(Monat)'}</span><span>{chf(preview.net_total)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">MWST {preview.mwst_rate ? `(${preview.mwst_rate}%)` : ''}</span><span>{chf(preview.mwst_amount)}</span></div>
              <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-bold text-slate-900"><span>Total</span><span data-testid="preview-total">{chf(preview.total)}</span></div>
              {tState && (
                <div className="mt-4 space-y-2 border-t border-slate-200 pt-3 text-xs">
                  <div className="flex items-center gap-2 text-slate-600"><CalendarClock className="h-3.5 w-3.5" /> Abo: <b>{SUB_STATUS[tState.status] || tState.status}</b></div>
                  {tState.status === 'canceling' && <div className="text-amber-600">Kündigung wirksam zum {d(tState.cancel_effective_at)}</div>}
                  {tState.billing_suspended && <div className="text-red-600">Zugang wegen Zahlungsverzug gesperrt</div>}
                  <div className="pt-1">
                    {tState.status === 'canceling'
                      ? <button data-testid="billing-revoke-cancel-btn" onClick={revokeCancel} disabled={saving === 'cancel'} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><RotateCcw className="h-3.5 w-3.5" /> Kündigung zurücknehmen</button>
                      : (tState.status !== 'none' && tState.status !== 'cancelled') && <button data-testid="billing-cancel-btn" onClick={cancelTenant} disabled={saving === 'cancel'} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"><XCircle className="h-3.5 w-3.5" /> Abo kündigen</button>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Fallback-Kommunikation (Invicta) */}
      <Card>
        <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-900"><Mail className="h-5 w-5" /> Fallback-Kommunikation (Invicta)</h2>
        <p className="mb-4 text-xs text-slate-500">Zentraler Absender für alle Mandanten-Mails & WhatsApp an deren Endkunden. Greift, wenn der Mandant keine eigenen Zugangsdaten hinterlegt hat <b>oder</b> der Versand fehlschlägt. Bei E-Mails bleibt der Studioname als Anzeigename erhalten.</p>
        <div className="grid gap-6 lg:grid-cols-2">
          {/* E-Mail */}
          {email && (
            <div className="rounded-xl border border-slate-200 p-4" data-testid="fallback-email-card">
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Mail className="h-4 w-4" /> E-Mail (SMTP)</span>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input data-testid="fallback-email-enabled" type="checkbox" checked={!!email.enabled} onChange={(e) => setE('enabled', e.target.checked)} /> aktiv
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="SMTP-Host"><input data-testid="fallback-email-host" className={inputCls} value={email.smtp_host || ''} onChange={(e) => setE('smtp_host', e.target.value)} /></Field>
                <Field label="Port"><input data-testid="fallback-email-port" className={inputCls} value={email.smtp_port || ''} onChange={(e) => setE('smtp_port', e.target.value)} /></Field>
                <Field label="Benutzer"><input data-testid="fallback-email-user" className={inputCls} value={email.smtp_username || ''} onChange={(e) => setE('smtp_username', e.target.value)} /></Field>
                <Field label="Passwort" hint={email.password_set ? 'gesetzt — leer lassen = unverändert' : 'nicht gesetzt'}><input data-testid="fallback-email-pass" type="password" className={inputCls} value={email.smtp_password || ''} onChange={(e) => setE('smtp_password', e.target.value)} placeholder="••••••" /></Field>
                <Field label="Absender-Adresse"><input data-testid="fallback-email-from" className={inputCls} value={email.smtp_from_email || ''} onChange={(e) => setE('smtp_from_email', e.target.value)} /></Field>
                <Field label="Absender-Name (Default)"><input data-testid="fallback-email-fromname" className={inputCls} value={email.smtp_from_name || ''} onChange={(e) => setE('smtp_from_name', e.target.value)} /></Field>
                <Field label="Reply-To" hint="leer = Mandanten-Adresse"><input data-testid="fallback-email-replyto" className={inputCls} value={email.reply_to || ''} onChange={(e) => setE('reply_to', e.target.value)} /></Field>
              </div>
              <button data-testid="fallback-email-save" onClick={saveEmail} disabled={saving === 'email'} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">{saving === 'email' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern</button>
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="mb-1.5 text-xs font-medium text-slate-500">Testnachricht senden</div>
                <div className="flex flex-wrap items-center gap-2">
                  <input data-testid="fallback-email-test-to" type="email" className={`${inputCls} flex-1 min-w-[180px]`} value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder="empfaenger@beispiel.ch" />
                  <button data-testid="fallback-email-test-btn" onClick={sendTestEmail} disabled={saving === 'test-email'} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">{saving === 'test-email' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Senden</button>
                </div>
              </div>
            </div>
          )}
          {/* WhatsApp */}
          {wa && (
            <div className="rounded-xl border border-slate-200 p-4" data-testid="fallback-wa-card">
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><MessageSquare className="h-4 w-4" /> WhatsApp (Meta Cloud API)</span>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input data-testid="fallback-wa-enabled" type="checkbox" checked={!!wa.enabled} onChange={(e) => setW('enabled', e.target.checked)} /> aktiv
                </label>
              </div>
              <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="fallback-wa-connect-block">
                <div className="mb-2 text-xs text-emerald-900">Ein-Klick-Verbindung über Meta — Nummer, WABA-ID & Token werden automatisch übernommen.</div>
                <WhatsAppConnectButton
                  testId="fallback-wa-connect"
                  configUrl={`${BACKEND}/api/superadmin/billing/whatsapp-embedded/config`}
                  callbackUrl={`${BACKEND}/api/superadmin/billing/whatsapp-embedded/callback`}
                  headers={{ Authorization: `Bearer ${localStorage.getItem('token')}` }}
                  onConnected={() => superAdminApi.billingWhatsappSettings().then((r) => setWa(r.data))}
                />
              </div>
              <div className="grid gap-3">
                <Field label="Phone Number ID"><input data-testid="fallback-wa-phoneid" className={inputCls} value={wa.phone_id || ''} onChange={(e) => setW('phone_id', e.target.value)} /></Field>
                <Field label="WhatsApp Business Account ID (WABA)" hint="Für Vorlagen-Einreichung bei Meta nötig"><input data-testid="fallback-wa-waba" className={inputCls} value={wa.business_account_id || ''} onChange={(e) => setW('business_account_id', e.target.value)} placeholder="z. B. 344124728795281" /></Field>
                <Field label="Access Token" hint={wa.token_set ? 'gesetzt — leer lassen = unverändert' : 'nicht gesetzt'}><input data-testid="fallback-wa-token" type="password" className={inputCls} value={wa.access_token || ''} onChange={(e) => setW('access_token', e.target.value)} placeholder="••••••" /></Field>
                <Field label="Anzeige-Nummer (Info)"><input data-testid="fallback-wa-number" className={inputCls} value={wa.display_number || ''} onChange={(e) => setW('display_number', e.target.value)} placeholder="+41 ..." /></Field>
              </div>
              <button data-testid="fallback-wa-save" onClick={saveWa} disabled={saving === 'wa'} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">{saving === 'wa' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern</button>
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="mb-1.5 text-xs font-medium text-slate-500">Testnachricht senden</div>
                <div className="flex flex-wrap items-center gap-2">
                  <input data-testid="fallback-wa-test-to" className={`${inputCls} flex-1 min-w-[180px]`} value={testWaTo} onChange={(e) => setTestWaTo(e.target.value)} placeholder="+41 79 123 45 67" />
                  <button data-testid="fallback-wa-test-btn" onClick={sendTestWa} disabled={saving === 'test-wa'} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">{saving === 'test-wa' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Senden</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Rechnungen */}
      <Card>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900"><Receipt className="h-5 w-5" /> Rechnungen</h2>
        {invoices.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="invoices-empty">Noch keine Rechnungen. Nutze „Jetzt abrechnen" oder erzeuge eine Rechnung pro Mandant.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                <th className="px-3 py-2">Nr.</th><th className="px-3 py-2">Mandant</th><th className="px-3 py-2">Periode</th>
                <th className="px-3 py-2">Fällig</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {invoices.map((i) => {
                  const st = STATUS[i.status] || { label: i.status, cls: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={i.id} className="border-b border-slate-100" data-testid={`invoice-row-${i.number}`}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{i.number}</td>
                      <td className="px-3 py-2 text-slate-700">{i.tenant_slug || i.tenant_id}</td>
                      <td className="px-3 py-2 text-slate-500">{i.period_key} {i.interval === 'year' ? '(Jahr)' : ''}</td>
                      <td className="px-3 py-2 text-slate-500">{d(i.due_at)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-900">{chf(i.total)}</td>
                      <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}{i.dunning_level > 0 ? ` · M${i.dunning_level}` : ''}</span></td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <button data-testid={`inv-pdf-${i.number}`} onClick={() => downloadPdf(i)} disabled={saving === 'pdf-' + i.id} title="PDF herunterladen" className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50">{saving === 'pdf-' + i.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>
                          {i.status !== 'void' && (
                            <button data-testid={`inv-email-${i.number}`} onClick={() => sendInvoiceEmail(i)} disabled={saving === 'mail-' + i.id} title={i.email_sent_at ? 'Erneut per E-Mail senden' : 'Per E-Mail senden'} className={`rounded-lg border p-1.5 hover:bg-indigo-50 ${i.email_sent_at ? 'border-indigo-200 text-indigo-500' : 'border-slate-200 text-slate-600'}`}>{saving === 'mail-' + i.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
                          )}
                          {i.status !== 'paid' && i.status !== 'void' && (
                            <>
                              <button data-testid={`inv-paid-${i.number}`} onClick={() => markPaid(i.id)} disabled={saving === 'inv-' + i.id} title="Als bezahlt markieren" className="rounded-lg border border-emerald-200 p-1.5 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /></button>
                              <button data-testid={`inv-void-${i.number}`} onClick={() => voidInv(i.id)} disabled={saving === 'inv-' + i.id} title="Stornieren" className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><Ban className="h-4 w-4" /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Mahnungen */}
      {dunning.length > 0 && (
        <Card>
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900"><AlertTriangle className="h-5 w-5 text-red-500" /> Mahn-Ereignisse</h2>
          <div className="space-y-2" data-testid="dunning-list">
            {dunning.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <span className="text-slate-700">
                  <b className="font-mono text-xs">{e.number}</b> · {e.tenant_slug} · {e.action === 'suspend' ? 'Sperre' : `Mahnstufe ${e.level}`} · {e.days_overdue} T. überfällig
                </span>
                <span className="text-xs text-slate-400">{d(e.created_at)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
